<script setup lang="ts">
  import { Button, Popover, Typo } from '@travelata-libs/ui-kit';
  import { useViewport } from '@travelata-libs/utils';
  import { ref, watch } from 'vue';
  import DirectionOptionItem from './DirectionOptionItem.vue';
  import type { DirectionOption, Option } from '../Transfer.types';

  interface DirectionOptionsProps {
    value: `${DirectionOption}`;
    options: Option[];
    info: string;
    loading?: boolean;
  }

  interface DirectionOptionsEmits {
    (e: 'save', value: DirectionOption): void;
  }

  const props = defineProps<DirectionOptionsProps>();

  const emit = defineEmits<DirectionOptionsEmits>();

  const innerValue = ref(props.value);

  const popoverRef = ref(null);

  const onSave = () => {
    if (innerValue.value !== props.value) {
      emit('save', innerValue.value as DirectionOption);
    }

    popoverRef.value?.close();
  };

  const onClose = () => {
    innerValue.value = props.value;
  };

  watch(
    () => props.value,
    (value) => {
      innerValue.value = value;
    },
  );

  const { isMobile } = useViewport();
</script>

<template>
  <div class="tl-transfer-options">
    <Popover ref="popoverRef" placement="bottom-end" :mobile="isMobile" @close="onClose">
      <template #reference>
        <Button size="m" type="icon" icon="more" :disabled="loading" appearance="secondary" />
      </template>

      <template #header>
        <Typo variant="h-5">Выберите опции трансфера</Typo>
      </template>

      <div class="tl-transfer-options__content">
        <Typo v-if="isMobile" class="tl-transfer-options__info" variant="body-m-400">{{ info }}</Typo>

        <div class="tl-transfer-options__items">
          <DirectionOptionItem v-for="option in options" :key="option.value" v-model="innerValue" :option="option" />
        </div>

        <Button size="m" @click="onSave">Сохранить</Button>
      </div>
    </Popover>
  </div>
</template>

<style lang="scss" scoped>
  .tl-transfer-options {
    &__content {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-m);
      margin-top: var(--spacing-xxs);
      width: 445px;

      @media (max-width: $breakpoint-mobile-max) {
        gap: var(--spacing-xl);
        overflow-x: hidden;
        width: auto;
      }
    }

    &__info {
      color: var(--color-text-secondary);
    }

    &__items {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-m);

      @media (max-width: $breakpoint-mobile-max) {
        flex-direction: row;
        gap: 0;
        overflow-x: scroll;
      }
    }
  }
</style>
